import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Phone, Copy, Check, Radio, AlertCircle, Loader2, UserPlus
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { BACKEND_URL } from "../context/AuthContext";

const API = BACKEND_URL;

export default function CallInPage() {
  const [params] = useSearchParams();
  const roomId = params.get("room");
  const [roomInfo, setRoomInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(null);
  const [callbackPhone, setCallbackPhone] = useState("");
  const [callbackName, setCallbackName] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    if (!roomId) { setLoading(false); return; }
    fetch(`${API}/api/callin/active/${encodeURIComponent(roomId)}`)
      .then(r => r.json())
      .then(d => { if (d.active) setRoomInfo(d); else setError("Room not found or no longer active."); })
      .catch(() => setError("Could not check room status."))
      .finally(() => setLoading(false));
  }, [roomId]);

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const requestCallback = async () => {
    if (!callbackPhone.trim() || !callbackName.trim()) return setError("Enter your name and phone number");
    setRequesting(true); setError("");
    try {
      const r = await fetch(`${API}/api/callin/request-callback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ room_id: roomId, name: callbackName, phone: callbackPhone }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Request failed");
      setRequested(true);
    } catch (e) { setError(e.message); }
    setRequesting(false);
  };

  const downloadVCard = () => {
    if (!roomInfo) return;
    const station = (roomInfo.station_name || "Call-In").replace(/[;,\\]/g, " ").trim();
    const fullName = `${station} Call-In`;
    const phone = roomInfo.phone_number;
    const note = `Room code: ${roomInfo.room_id}. Call the number, press 1, then enter the room code followed by #.`;
    const vcard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${fullName}`,
      `N:Call-In;${station};;;`,
      `ORG:${station}`,
      `TEL;TYPE=CELL,VOICE:${phone}`,
      `NOTE:${note.replace(/\n/g, "\\n")}`,
      "END:VCARD",
    ].join("\r\n");
    const safeName = station.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "callin";
    const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeName}-callin.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (!roomId) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="max-w-md mx-auto px-4 pt-32 pb-20 text-center">
          <Phone className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <h1 className="text-white text-xl font-bold mb-2">Call-In Studio</h1>
          <p className="text-white/40 text-sm">No room specified. You need a call-in link from the host.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <SEO title="Join Live Call-In — The NU Vibe Network" />
      <Header />

      <div className="max-w-md mx-auto px-4 pt-28 pb-20">
        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto" /></div>
        ) : error && !roomInfo ? (
          <div className="text-center py-16">
            <AlertCircle className="w-10 h-10 text-red-400/50 mx-auto mb-3" />
            <p className="text-red-400/80 text-sm">{error}</p>
          </div>
        ) : roomInfo ? (
          <>
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-purple-500/20 mx-auto mb-4 flex items-center justify-center">
                <Radio className="w-7 h-7 text-purple-400" />
              </div>
              <h1 className="text-white text-2xl font-bold mb-1">Join Live Call-In</h1>
              <p className="text-white/40 text-sm">{roomInfo.station_name}</p>
              {roomInfo.status === "live" && (
                <span className="inline-flex items-center gap-1.5 bg-red-500/20 text-red-400 text-xs font-bold px-3 py-1 rounded-full mt-3">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" /> LIVE NOW
                </span>
              )}
            </div>

            {/* Option 1: Call the number */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 mb-4" data-testid="callin-phone-option">
              <h3 className="text-white font-semibold text-sm mb-3">Option 1: Call In</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-white/[0.02] rounded-lg px-4 py-3">
                  <div>
                    <p className="text-white/40 text-xs">Phone Number</p>
                    <p className="text-white text-lg font-mono font-bold">{roomInfo.phone_number}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`tel:${roomInfo.phone_number}`}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-xs font-semibold transition flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" /> Call
                    </a>
                    <button onClick={() => copyText(roomInfo.phone_number, "phone")}
                      className="text-white/30 hover:text-white/60 transition p-2">
                      {copied === "phone" ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-white/[0.02] rounded-lg px-4 py-3">
                  <div>
                    <p className="text-white/40 text-xs">Room Code</p>
                    <p className="text-white text-lg font-mono font-bold">{roomInfo.room_id}</p>
                  </div>
                  <button onClick={() => copyText(roomInfo.room_id, "code")}
                    className="text-white/30 hover:text-white/60 transition p-2">
                    {copied === "code" ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-white/20 text-xs">Call the number, press 1, then enter the room code followed by #</p>
                <button
                  onClick={downloadVCard}
                  className="w-full mt-1 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] text-white/80 hover:text-white font-medium py-2.5 rounded-lg transition text-xs flex items-center justify-center gap-2"
                  data-testid="save-contact-btn"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Save to Contacts
                </button>
              </div>
            </div>

            {/* Option 2: Request a callback */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5" data-testid="callin-callback-option">
              <h3 className="text-white font-semibold text-sm mb-3">Option 2: Have Them Call You</h3>
              {requested ? (
                <div className="text-center py-4">
                  <Check className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <p className="text-green-400 text-sm font-medium">Request sent! The host will call you shortly.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <input type="text" value={callbackName} onChange={(e) => setCallbackName(e.target.value)}
                    placeholder="Your name"
                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg py-2.5 px-4 text-white placeholder-white/20 text-sm focus:outline-none focus:border-purple-500/50"
                    data-testid="callback-name" />
                  <input type="tel" value={callbackPhone} onChange={(e) => setCallbackPhone(e.target.value)}
                    placeholder="+1 555 123 4567"
                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg py-2.5 px-4 text-white placeholder-white/20 text-sm focus:outline-none focus:border-purple-500/50"
                    data-testid="callback-phone" />
                  <button onClick={requestCallback} disabled={requesting}
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2.5 rounded-lg transition text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="request-callback-btn">
                    {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                    Request Callback
                  </button>
                  <p className="text-white/20 text-xs text-center">The host will see your request and call you into the show</p>
                </div>
              )}
              {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
            </div>
          </>
        ) : null}
      </div>
      <Footer />
    </div>
  );
}
